/** Session flag to avoid infinite reload when a chunk is genuinely missing. */
export const CHUNK_RELOAD_SESSION_KEY = 'ltda-chunk-reload-attempted';
const CHUNK_RELOAD_RESET_DELAY_MS = 15_000;

export function isStaleChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String(error);

  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('Unable to preload CSS') ||
    (error instanceof Error && error.name === 'ChunkLoadError')
  );
}

function clearPersistedState(): void {
  try {
    localStorage.removeItem('persist:root');
  } catch {
    // ignore
  }
}

export function hasAttemptedChunkReload(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

let chunkReloadAttemptedAtStartup = false;

export function captureChunkReloadAttemptAtStartup(): void {
  chunkReloadAttemptedAtStartup = hasAttemptedChunkReload();
}

export function didAttemptChunkReloadBeforeThisDocument(): boolean {
  return chunkReloadAttemptedAtStartup;
}

export function tryReloadForStaleChunk(error: unknown): boolean {
  if (!isStaleChunkLoadError(error) || hasAttemptedChunkReload()) {
    return false;
  }

  clearPersistedState();

  try {
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
  } catch {
    // Still reload. A later failure can try once more if the flag could not be stored.
  }

  window.requestAnimationFrame(() => {
    window.location.reload();
  });
  return true;
}

/** After a certain delay, clear the chunk reload flag, so later failures can try the reload again */
export function scheduleChunkReloadFlagReset(): void {
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
    } catch {
      // ignore
    }
  }, CHUNK_RELOAD_RESET_DELAY_MS);
}
